import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// material
import { styled } from '@mui/material/styles';
import { Box, Link, Drawer, Button, MenuItem, Typography, useTheme, Divider } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
import { selectUser } from '../../pages/login/selectors';
// components
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import WorkspaceSelectorDropdown from '../../components/workspace-selector-dropdown';
import { clearSession } from '../../session/actions';
import { selectSession } from '../../session/selectors';
import { refreshUser } from '../../pages/login/actions';
import { hasNotifications } from '../../entities/notification-type';
import palette from '../../theme/palette';
import NavConfig from './NavConfig';
import LoggedCard from '../../components/logged-card';
import PopoverMenu from '../../components/logged-card/popover-menu';
import ManageUserNotificationsDialog from '../../components/users/manage-user-notifications-dialog';
import EditUserDialog from '../../components/users/edit-user-dialog';


// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
}));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [anchor, setAnchor] = useState(null);
  const [openNotificationsManager, setOpenNotificationsManager] = useState(false);
  const [openEditProfile, setOpenEditProfile] = useState(false);
  const isDesktop = useResponsive('up', 'lg');

  const user = useSelector(selectUser());
  const session = useSelector(selectSession());

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <WorkspaceSelectorDropdown />
      <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.12)', mx: 2, mb: 2 }} />

      <NavSection navConfig={NavConfig()} />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <ManageUserNotificationsDialog
          user={user}
          open={openNotificationsManager}
          onClose={() => {
            setOpenNotificationsManager(false)
          }}
          onConfirm={() => {
            setOpenNotificationsManager(false)
            dispatch(refreshUser(dispatch, session))
          }} />
        <EditUserDialog
          title={t('sidebar.edit-profile')}
          value={user}
          open={openEditProfile}
          onClose={() => {
            setOpenEditProfile(false)
          }}
          onEdited={() => {
            setOpenEditProfile(false)
            dispatch(refreshUser(dispatch, session))
          }} />
        <Link underline="none" component={RouterLink} to="#">
          <AccountStyle>
            <Button fullWidth onClick={(event) => setAnchor(event.target)}>
              <LoggedCard user={user} />
            </Button>
          </AccountStyle>
        </Link>
        <PopoverMenu
          anchor={anchor}
          setAnchor={setAnchor}
          children={(
            <>
              <AccountStyle style={{ backgroundColor: palette.background.neutral, padding: '8px 18px', margin: 15 }}>
                <LoggedCard user={user} onEdit={() => {
                  setAnchor(null);
                  setOpenEditProfile(true);
                }} />
              </AccountStyle>
              {user && hasNotifications(user.role) && (
                <MenuItem
                  onClick={() => {
                    setAnchor(null)
                    setOpenNotificationsManager(true);
                  }}
                  style={{ padding: '5px 15px 10px 15px' }}
                >
                  <Typography variant='button' style={{ color: theme.palette.primary.main }}>{t('sidebar.manage-notifications')}</Typography>
                </MenuItem>)
              }
              <MenuItem
                component={RouterLink}
                to="/dashboard/credits"
                onClick={() => setAnchor(null)}
                style={{ padding: '5px 15px 10px 15px' }}
              >
                <Typography variant='button' style={{ color: theme.palette.primary.main }}>{t('sidebar.credits')}</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  dispatch(clearSession());
                  localStorage.clear();
                }}
                style={{ padding: '5px 15px 10px 15px' }}
              >
                <Typography variant='button' style={{ color: theme.palette.error.main }}>{t('sidebar.logout')}</Typography>
              </MenuItem>
            </>
          )}
        />
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle>
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'secondary.main',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
};